"use client"

import styled from "@emotion/styled"
import { Box } from "@mui/material"
import { useEffect, useState } from "react"
import { useInView } from "react-intersection-observer"
import { mediaQueries, useInViewConfig, useTransitionUpStyles } from "ui"
import ActionLinkCard from "ui/common/ActionLinkCard"
import DetailsCard from "ui/common/DetailsCard"
import { colors, sizes } from "ui/theme/theme"
import { extractBlockProperties } from "utils/simplifyData"

const StyledContentWrapper = styled(Box)`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: ${sizes.baseTriple};
  ${mediaQueries.desktopM} {
    flex-direction: row;
    justify-content: space-between;
  }
`

const StyledCardsWrapper = styled(Box)`
  display: flex;
  flex-flow: row wrap;
  gap: ${sizes.baseDouble};
  width: 100%;
  ${mediaQueries.desktopM} {
    width: 60%;
    border-inline-end: 1px solid ${colors.borderGray};
  }
`

const StyledActionLinkCard = styled(ActionLinkCard, {
  shouldForwardProp: (prop) =>
    prop !== "smallDesktopCardWidth" && prop !== "desktopCardWidth",
})<{ smallDesktopCardWidth: string; desktopCardWidth: string }>`
  width: ${(props) => props.smallDesktopCardWidth};
  height: auto;
  box-shadow: none;
  justify-content: flex-start;
  :hover {
    box-shadow: ${colors.dropShadow.main};
  }
  ${mediaQueries.desktop} {
    width: ${(props) => props.desktopCardWidth};
  }
`

interface ActionLinkCardSelectorProps {
  itemsList: any
  emptyMessage?: string
  cardKeyPrefix?: string
  icons?: any
  isIconInTitle?: boolean
  smallDesktopCardWidth?: string
  desktopCardWidth?: string
  className?: string
}

const ActionLinkCardSelector = ({
  itemsList,
  icons,
  isIconInTitle = true,
  smallDesktopCardWidth = "189px",
  desktopCardWidth = "220px",
  className,
}: ActionLinkCardSelectorProps) => {
  const { ref, inView } = useInView(useInViewConfig)
  const transitionStyles = useTransitionUpStyles(inView)
  const itemsListBlocks = extractBlockProperties(itemsList?.blocks)
  // Use index for selection instead of item object
  const [selectedIndex, setSelectedIndex] = useState<number>(0)

  // Set the first item as selected by default when component mounts

  useEffect(() => {
    if (itemsList?.blocks?.length > 0) {
      setSelectedIndex(0)
    }
  }, [itemsList?.blocks])

  const handleCardClick = (_item: any, idx: number) => {
    setSelectedIndex(idx)
  }

  return (
    <StyledContentWrapper
      className={className}
      ref={ref}
      style={transitionStyles}
    >
      <StyledCardsWrapper className="action-link-card-selector">
        {itemsListBlocks?.map((item: any, index: number) => {
          const { primaryColor, icon, title } = item
          return (
            <StyledActionLinkCard
              data={{
                onClick: () => handleCardClick(item, index),
                icon: icon?.src,
                title: title,
                primaryColor: primaryColor || "00A7B5",
                index,
              }}
              key={`action-link-card-card-${index}`}
              uniqueKeyPrefix={`action-link-card-${index}`}
              titleComponent="h3"
              smallDesktopCardWidth={smallDesktopCardWidth}
              desktopCardWidth={desktopCardWidth}
            />
          )
        })}
      </StyledCardsWrapper>
      {/* Mount all DetailsCards for SEO, only show the selected one */}
      {itemsListBlocks?.map((item: any, index: number) => {
        const {
          title,
          icon,
          address,
          phone,
          email,
          fax,
          additionalPhone,
          whatsapp,
          subtitle,
          text,
          primaryColor,
          itemsList: detailsItemsList,
        } = item
        const isSelected = selectedIndex === index

        return (
          <DetailsCard
            key={`details-card-${index}`}
            data={{
              title: title,
              itemsList: detailsItemsList,
              icon: isIconInTitle ? icon?.src : undefined,
              subtitle: subtitle,
              text: text,
              detailsContent: [
                {
                  ...phone,
                  additional: { ...additionalPhone },
                  icon: icons?.phone,
                },
                {
                  ...whatsapp,
                  icon: icons?.whatsapp,
                },
                { ...fax, icon: icons?.fax },
                {
                  ...email,
                  icon: icons?.email,
                },
                {
                  ...address,
                  icon: icons?.address,
                },
              ].filter(Boolean),
              primaryColor: primaryColor || "00A7B5",
            }}
            hidden={!isSelected}
          />
        )
      })}
    </StyledContentWrapper>
  )
}

export default ActionLinkCardSelector
